import { getMaterialColor } from "@/features/projects/selection-sheet/material-types"

/** The 7 template material keys the API stores (matches `MATERIAL_TYPES` in the backend). */
export const MATERIAL_TYPES = ["tile", "wood", "lvp_lvt", "laminate", "rubber", "carpet", "other"] as const

export type MaterialTypeKey = (typeof MATERIAL_TYPES)[number]

/** Display label per key — the same names the selection sheet uses. */
export const MATERIAL_TYPE_LABELS: Record<MaterialTypeKey, string> = {
	tile: "Tile",
	wood: "Wood",
	lvp_lvt: "LVP/LVT",
	laminate: "Laminate",
	rubber: "Rubber",
	carpet: "Carpet",
	other: "Other"
}

/** Locked product-spec color for a material key (reuses the selection-sheet palette). */
export function materialTypeColor(key: MaterialTypeKey): string {
	return getMaterialColor(MATERIAL_TYPE_LABELS[key])
}

export function isMaterialTypeKey(value: unknown): value is MaterialTypeKey {
	return typeof value === "string" && (MATERIAL_TYPES as readonly string[]).includes(value)
}

/**
 * A material row. Backend `Decimal` columns serialize to JSON **strings** (Pydantic v2),
 * so every numeric here is `string | null` — format for display, never do math on it directly.
 */
export type MaterialPublic = {
	id: number
	account_id: number
	parent_id: number | null
	material_type: MaterialTypeKey | null
	manufacturer: string | null
	style_name: string | null
	color: string | null
	size: string | null
	finish: string | null
	thickness: string | null
	width: string | null
	sku: string | null
	sf_per_box: string | null
	cost: string | null
	price: string | null
	photo_url: string | null
	is_dropped: boolean
	custom_price_count: number
	created_at: string | null
	updated_at: string | null
}

/** One original plus its private labels — the list endpoint paginates originals only. */
export type MaterialListItem = MaterialPublic & {
	children: MaterialPublic[]
}

/**
 * A flattened table row. Parents and their private labels live in one list marked by `isChild`
 * — server pagination is parent-driven, so a TanStack expansion model would buy nothing.
 */
export type MaterialRow = MaterialPublic & {
	isChild: boolean
}

export type MaterialListResponse = {
	items: MaterialListItem[]
	total: number
	limit: number
	offset: number
}

export type MaterialsListQuery = {
	q?: string
	limit: number
	offset: number
	sort?: string
	material_type?: MaterialTypeKey
	include_dropped?: boolean
}

/** Create/update payload — every field is optional on the API. */
export type MaterialWriteBody = {
	material_type?: MaterialTypeKey | null
	manufacturer?: string | null
	style_name?: string | null
	color?: string | null
	size?: string | null
	finish?: string | null
	thickness?: string | null
	width?: string | null
	sku?: string | null
	sf_per_box?: string | null
	cost?: string | null
	price?: string | null
	photo_url?: string | null
	is_dropped?: boolean
}

export type MaterialClientPrice = {
	id: number
	material_id: number
	client_id: number
	client_name: string
	price: string
	created_at: string | null
	updated_at: string | null
}

export type MaterialClientPriceListResponse = {
	items: MaterialClientPrice[]
}

export type MaterialClientPriceUpsertBody = {
	client_id: number
	price: string
}

export type MaterialPhotoPresign = {
	url: string
	method: string
	headers: Record<string, string>
	public_url: string
}

export type MaterialImportPresign = {
	url: string
	method: string
	headers: Record<string, string>
	object_key: string
}

export type MaterialImportFile = {
	object_key: string
	content_type: string
	/** PDFs only: the 1-based pages to read; null reads the whole file. */
	selected_pages: number[] | null
}

export type MaterialImportStarted = {
	task_id: string
}

export type MaterialImportStatus = {
	status: "pending" | "running" | "succeeded" | "failed"
	imported_count: number | null
	error_count: number | null
}
